import { Box } from "@mui/material";

import { isDesktop, isHeaderDesktop, isSmallDesktop, isTabletUp } from "@hooks/layout";

import styled, { css } from "styled-components";

type TContainer = {
  $isPage: boolean;
  $withBanner: boolean;
  $isHero: boolean;
  $minHeight: number;
  $isBackgroundPositionBottom: boolean;
};

type TContentContainer = {
  $isCentered: boolean;
} & Pick<TContainer, "$isHero">;

export const Container = styled(Box)<TContainer>`
  position: relative;
  width: 100%;
  height: ${({ $isPage }) => ($isPage ? "auto" : "100%")};
  /* Phone: size to content so short sections don't stretch to 812 and dump the
     slack at the bottom (breaks top/bottom symmetry). Keep the 812 floor from
     tablet up where it gives full, balanced sections. */
  min-height: ${({ $isPage }) => ($isPage ? "50dvh" : "auto")};

  ${isTabletUp} {
    min-height: ${({ $isPage }) => ($isPage ? "50dvh" : "812px")};
  }

  overflow-x: hidden;

  ${({ $minHeight }) =>
    !!$minHeight &&
    css`
      min-height: ${`${$minHeight}px`};
    `}

  padding-top: 48px;
  padding-bottom: 48px;

  background-color: #715137;

  display: flex;

  ${({ $isHero }) =>
    $isHero &&
    css`
      height: 100dvh;
      width: 100dvw;
    `}
  ${isSmallDesktop} {
    ${({ $isPage }) =>
      $isPage
        ? css`
            /* 1024–1149: the mobile header is still showing (sticky, in flow),
               so DON'T add the fixed-desktop-header top reservation yet — keep
               the small mobile top padding. The real desktop top padding kicks
               in at the 1150 header swap (isHeaderDesktop) below. Inline/bottom
               already take their desktop values here. */
            padding: 48px 0 176px;
          `
        : css`
            padding-block: 160px;
          `}
    ${({ $isHero }) =>
      $isHero &&
      css`
        height: 100dvh;
      `}
  }

  ${isHeaderDesktop} {
    ${({ $withBanner, $isPage }) =>
      $isPage &&
      css`
        padding-top: ${$withBanner ? "72px" : "96px"};
      `}
  }
`;

type TBackgroundLayer = {
  $isPage: boolean;
  $isBackgroundPositionBottom: boolean;
};

export const BackgroundLayer = styled.div<TBackgroundLayer>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  max-height: ${({ $isPage }) => ($isPage ? "none" : "100vh")};
  z-index: 0;
  pointer-events: none;
  background-position-y: ${({ $isPage }) => ($isPage ? "top" : "center")};
  background-position-x: center;
  background-size: cover;
  background-repeat: no-repeat;

  ${isSmallDesktop} {
    ${({ $isPage }) =>
      $isPage &&
      css`
        background-position-x: 65%;
      `}
  }

  ${({ $isBackgroundPositionBottom }) =>
    $isBackgroundPositionBottom &&
    css`
      background-position-y: bottom;
    `}
`;

/** Bottom-to-brown transition for tall sections. Pinned to the top at
    ~calc(100vh - 200px) so the fade starts inside the image (around where
    the BackgroundLayer's 100vh cap ends), and stretches all the way to
    the section's bottom. Result: no hard edge between image and solid
    container brown AND no solid-brown band between the two. Opt-in via
    `withBottomFade` on PageContainer — only tall sections need it. */
export const BottomFade = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100vh - 220px);
  bottom: 0;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(113, 81, 55, 0) 0%,
    rgba(113, 81, 55, 1) 220px,
    rgba(113, 81, 55, 1) 100%
  );
`;

export const ContentContainer = styled.div<TContentContainer>`
  width: 100%;
  height: 100%;
  margin: 0 auto;

  z-index: 1;

  align-self: start;

  padding-inline: 16px;

  max-width: 1280px;
  max-height: 100%;

  ${isSmallDesktop} {
    align-self: start;
    padding-inline: 40px;
    ${({ $isHero }) =>
      $isHero &&
      css`
        display: flex;
        align-items: center;
      `}
  }

  ${isDesktop} {
    padding-inline: 40px;
  }

  && {
    ${({ $isCentered }) =>
      $isCentered &&
      css`
        ${isSmallDesktop} {
          padding-inline: 0;
          max-width: 100%;
        }
      `}
  }
`;
